import React, { useEffect, useRef, useMemo } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import * as S from './Carousel.styled';
import { AdKind } from '@/api/types/common.types';
import { PhotoOverlay } from '@/components/PhotoOverlay';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { KindIcons } from '@/components/KindIcons/KindIcons';
import { useGalleryStore } from '@/store/useGalleryStore';

export const Carousel = ({
  images,
  loopSingleImage = false,
  title,
  dataStat,
  kinds,
  loading,
  sliderId,
  openGallery,
  overlayText,
}: {
  loading: boolean;
  images: string[];
  title: string;
  loopSingleImage: boolean;
  sliderId: string;
  dataStat: string;
  kinds?: AdKind[];
  openGallery?: () => void;
  overlayText?: string;
}) => {
  const { currentIndex, setCurrentIndex, setSliderId } = useGalleryStore();
  const isInternalChangeRef = useRef(false);
  const totalSlides = images.length;
  const normalizedIndex =
    totalSlides > 0
      ? ((currentIndex % totalSlides) + totalSlides) % totalSlides
      : 0;
  const slidesCounter = `${normalizedIndex + 1} / ${totalSlides}`;

  useEffect(() => {
    setSliderId(sliderId);
    return () => {
      setSliderId(null);
    };
  }, [sliderId, setSliderId]);

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: true,
    slides: {
      perView: 1,
    },
    mode: 'snap',
    dragSpeed: 0.4,
    rubberband: false,
    renderMode: 'performance',
    slideChanged: () => {
      if (instanceRef.current && totalSlides > 0) {
        const newIndex = instanceRef.current.track.details.rel ?? 0;
        const absoluteIndex = instanceRef.current.track.details.abs ?? newIndex;
        const normalizedIdx =
          ((absoluteIndex % totalSlides) + totalSlides) % totalSlides;
        isInternalChangeRef.current = true;

        setCurrentIndex(normalizedIdx);
        setTimeout(() => {
          isInternalChangeRef.current = false;
        }, 0);
      }
    },
  });

  useEffect(() => {
    if (
      instanceRef.current &&
      !isInternalChangeRef.current &&
      totalSlides > 0
    ) {
      const currentAbs = instanceRef.current.track.details.abs ?? 0;
      const currentNormalized =
        ((currentAbs % totalSlides) + totalSlides) % totalSlides;

      if (currentNormalized !== normalizedIndex) {
        instanceRef.current.moveToIdx(normalizedIndex);
      }
    }
  }, [normalizedIndex, totalSlides]);

  const handleSlideClick = (idx: number) => {
    isInternalChangeRef.current = true;
    setCurrentIndex(idx);
    setTimeout(() => {
      isInternalChangeRef.current = false;
    }, 0);
    openGallery?.();
  };

  const slides = useMemo(() => {
    if (loopSingleImage) {
      return [...images, images[0]];
    }

    return images;
  }, [images, loopSingleImage]);

  return (
    <>
      <S.SliderWrapper $loading={loading}>
        {!!overlayText && <PhotoOverlay overlayText={overlayText} zIndex={2} />}
        <S.Slider
          ref={sliderRef}
          role="presentation"
          $photosCount={images.length}
          className="keen-slider"
        >
          {slides.map((src, i) => (
            <S.Slide key={`${i}-${src}`} className="keen-slider__slide">
              <S.SlideImage
                src={src}
                width={660}
                height={496}
                {...(dataStat ? { dataStat } : {})}
                {...(i ? { loading: 'lazy' } : { fetchpriority: 'high' })}
                onClick={() => handleSlideClick(i)}
                alt={title + (i ? ` (${i + 1})` : '')}
                data-testid={AdPageTestIds.slider.image}
              />
            </S.Slide>
          ))}
        </S.Slider>
        <KindIcons kinds={kinds} />

        <S.Count data-testid={AdPageTestIds.slider.counter}>
          {slidesCounter}
        </S.Count>
      </S.SliderWrapper>
      <div id={sliderId} />
    </>
  );
};
